import _ from "lodash";
import { renderToStaticMarkup } from "react-dom/server";

import { colorsPalettes, colorsSets } from "@similarweb/styles";

import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { hasAccessToPackage } from "app/common/services/solutions2Helper";
import { watermarkService } from "app/common/services/watermarkService";
import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";
import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";
import { CHART_COLORS } from "app/constants/ChartColors";
import { i18nFilter } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import {
    numbersFilter,
    percentDataFormat,
    percentsFilter,
} from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/ChannelsOverviewBarChartContainerFormat";
import { ChannelsOverviewBarChartTooltip } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/ChannelsOverviewBarChartTooltip";
import { ChannelsOverviewCategoryLabel } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/components/category-label/ChannelsOverviewCategoryLabel";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import utils, { orderedTrafficSources, trafficSources as trafficSourcesDefinitons } from "app/Shared/utils";

import { NoDataWrapper } from "./StyledComponents";

const i18n = i18nFilter();

export const CHANNELS_OVERVIEW_BAR_CHART_HEIGHT = 297;

const CHANNEL_SHORT_LABEL_KEY: Record<string, string> = {
    "Search - Organic": "channels.label.short.org.search",
    "Organic Search": "channels.label.short.org.search",
    "Search - Paid": "channels.label.short.paid.search",
    "Paid Search": "channels.label.short.paid.search",
    "Social - Organic": "channels.label.short.org.social",
    "Social - Paid": "channels.label.short.paid.social",
};

export const translateSourceText = (source: string): string => {
    const shortKey = CHANNEL_SHORT_LABEL_KEY[source];
    if (shortKey) {
        return i18n(shortKey);
    }
    return i18n(trafficSourcesDefinitons[source]?.title) || source;
};

export const getPointY = (pointValue = 0, totalVisits, dataFormat) => {
    switch (dataFormat) {
        case percentDataFormat:
            if (!totalVisits) {
                return null;
            } else {
                return 100 * (pointValue / totalVisits);
            }
        default:
            return pointValue;
    }
};

const getCategoryBarPointRange = (seriesCount) => {
    switch (seriesCount) {
        case 0:
            return 0.5;
        case 1:
            return 1;
        default:
            return 1.5;
    }
};

export const NoDataWrapperComp = ({ size }) => {
    return (
        <NoDataWrapper size={size}>
            <NoDataLandscape
                title="mmx.channelsoverview.bar.chart.no.data.title"
                subtitle="mmx.channelsoverview.bar.chart.no.data.subtitle"
            />
        </NoDataWrapper>
    );
};

export const getCategoryDataPoints = (data, dataFormat, seriesCount) => {
    const { text, id, trafficSources, totalVisits } = data.category;
    if (_.isEmpty(trafficSources)) {
        return [];
    } else {
        // Align to the same union of sources used by the xAxis categories so a missing
        // source in the category entry doesn't shift values into the wrong x position.
        const sourcesPresentInData = new Set<string>();
        Object.values<any>(data).forEach((entry) => {
            Object.keys(entry?.trafficSources ?? {}).forEach((s) => sourcesPresentInData.add(s));
        });
        const alignedSources = orderedTrafficSources.filter((source) => sourcesPresentInData.has(source));
        return [
            {
                name: i18n("workspace.marketing.arena.channels.overview.benchmark", {
                    category: text.toString(),
                }),
                id,
                grouping: false,
                zIndex: -1,
                pointWidth: 70,
                pointRange: getCategoryBarPointRange(seriesCount),
                pointPadding: 0.15,
                maxPointWidth: null,
                dataLabels: {
                    enabled: false,
                },
                data: alignedSources.map((sourceName) => {
                    const hasSource = Object.prototype.hasOwnProperty.call(trafficSources, sourceName);
                    return {
                        key: translateSourceText(sourceName),
                        y: hasSource ? getPointY(trafficSources[sourceName], totalVisits, dataFormat) : null,
                        color: colorsSets.b3.toArray()[0],
                        pointTooltipColor: "gray",
                    };
                }),
            },
        ];
    }
};

const getCategories = (data) => {
    const chartItemsInfo = utils.volumesAndSharesSplited;
    // we don't care about overriding category values. we just want to make sure we have all the keys
    const categoriesKeys = Array.from(
        Object.values<any>(data).reduce((acc, current) => {
            Object.keys(current.trafficSources).forEach((k) => acc.add(k));
            return acc;
        }, new Set([])),
    );
    const categories = categoriesKeys.map((key) => ({ key }));
    return _.sortBy(categories, chartItemsInfo.sort).map((cat: { key: string }) => {
        const key = cat.key;
        const item = chartItemsInfo.order[key];
        return {
            key,
            item,
            title: i18n(item?.title) || key,
            shortTitle: translateSourceText(key),
        };
    });
};

const getRenderCategoryLabel = () => {
    return function () {
        const { key, item, shortTitle } = this.value;
        const chosenTitle = i18n((item && item.title) || key);

        let href: string | null = null;
        if (item?.state) {
            const itemState = item.title === "utils.paidSearch" ? "digitalsuite_website_search_overview" : item.state;
            href = proNavigator.href(itemState, proNavigator.getParams());

            if (item?.queryParams) {
                href = `${href}&${item.queryParams}`;
            }

            const hasAccess =
                hasAccessToPackage("digitalsuite") ||
                (hasAccessToPackage("salesIntelligence") && salesSettingsHelper.isMarkettingChannelsAdonEnabled);

            if (!hasAccess) {
                href = null;
            }
        }

        return renderToStaticMarkup(
            <ChannelsOverviewCategoryLabel
                categoryKey={key}
                href={href}
                label={shortTitle}
                tooltip={chosenTitle}
            />,
        );
    };
};

export const getChartConfig = ({
    data,
    dataFormat,
    isCompare,
    isBenchmarkMode,
    showWatermark,
    height = CHANNELS_OVERVIEW_BAR_CHART_HEIGHT,
}: {
    data: any;
    dataFormat: string;
    isCompare: boolean;
    isBenchmarkMode: boolean;
    showWatermark: boolean;
    height?: number;
}) => {
    const config = {
        colors: Object.values(CHART_COLORS.trafficSourcesColorsBySourceMMX),
        chart: {
            height,
            type: "column",
            animation: false,
            spacingTop: 20,
            events: {
                load: function () {
                    if (showWatermark) {
                        watermarkService.add.call(this, { opacity: 0.1 });
                    }
                },
            },
        },
        xAxis: {
            categories: getCategories(data),
            lineColor: colorsPalettes.carbon[100],
            gridLineColor: colorsPalettes.carbon[50],
            crosshair: !isBenchmarkMode && isCompare,
            tickWidth: 0,
            labels: {
                align: "center",
                rotation: 0,
                useHTML: true,
                style: {
                    fontSize: "14px",
                    textAlign: "center",
                    fontFamily: '"Roboto", sans-serif',
                },
                formatter: getRenderCategoryLabel(),
            },
        },
        yAxis: {
            gridLineColor: "#e4e4e4",
            min: dataFormat === percentDataFormat ? 0 : null,
            max: dataFormat === percentDataFormat ? 100 : null,
            startOnTick: false,
            endOnTick: true,
            title: null,
            opposite: false,
            showLastLabel: true,
            color: colorsPalettes.carbon[400],
            labels: {
                align: "right",
                enabled: true,
                useHTML: true,
                x: -10,
                formatter() {
                    if (dataFormat === percentDataFormat) {
                        return this.value + percentDataFormat;
                    } else {
                        return abbrNumberFilter()(this.value);
                    }
                },
                style: {
                    fontSize: "12px",
                    textTransform: "uppercase",
                    fontFamily: '"Roboto", sans-serif',
                    color: colorsPalettes.carbon[400],
                },
            },
        },
        legend: {
            enabled: false,
        },
        plotOptions: {
            column: {
                borderWidth: 0,
                pointWidth: isCompare ? null : 50,
                minPointLength: 3,
                grouping: true,
                maxPointWidth: 40,
                pointPadding: 0.15,
                pointRange: 1,
                dataLabels: {
                    enabled: !isCompare && !isBenchmarkMode,
                    crop: false,
                    formatter: function () {
                        return this.point.y
                            ? dataFormat === percentDataFormat
                                ? percentsFilter(this.point.y)
                                : numbersFilter(this.point.y)
                            : "N/A";
                    },
                    color: "#707070",
                    useHTML: true,
                    style: {
                        fontFamily: '"Roboto", sans-serif',
                        fontSize: "14px",
                        fontWeight: "400",
                    },
                    overflow: "none",
                },
                states: {
                    hover: {
                        enabled: false,
                    },
                },
            },
        },
        tooltip: {
            enabled: true,
            shared: true,
            outside: true,
            followPointer: true,
            useHTML: true,
            ...tooltipContainerConfig().tooltip,
            formatter: function () {
                const xAxisCategories = this.points?.[0]?.series?.xAxis?.categories ?? [];
                const category = xAxisCategories[this.x] ?? this.x;
                const title = typeof category === "object" ? category?.title : category;
                return ChannelsOverviewBarChartTooltip({
                    points: this.points,
                    title,
                    dataFormat,
                    isCompare,
                    isBenchmarkMode,
                });
            },
        },
    };
    return config;
};
